import React, { useRef, useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import {
  getChartSizeConfig,
  getChartResponsiveConfig,
  getBreakpointChartConfig,
  ResponsiveChartConfigInput,
} from "@/utils/chart-responsive-utils";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { humanizeFieldName } from "@/utils";
import { formatAxisTick, formatNumericAxisTick } from "@/utils/PropertyFormatters";
import { ChartDataSource, ComputedField } from "@/types/chartDataSource";
import { enrichDataWithComputedFields } from "@/utils/computedFields";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartHoverStore } from "@/stores/chart/chart-hover-store";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { useComponentData } from "@/stores/editor/component-data-store";
import { computeTightNumericDomain, normalizeChartRows, sanitizeNumericDomain, sortChartRowsByXAxis } from "./chartDataUtils";
import { extractTimeZoneFromRecord, findRawTemporalField, getBrowserTimeZone, isTemporalFieldName, resolvePreferredTimeZone } from "@/utils/dateTimeFormatting";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface AreaChartProps {
  component: Component;
  isPreview?: boolean;
}

/**
 * Phase 4.3: Area chart stack offset types (mirrors backend)
 */
type AreaStackOffset = 'none' | 'expand' | 'wiggle' | 'silhouette';
type AreaChartVariant = 'standard' | 'percentage' | 'stream';
type ChartRow = Record<string, unknown>;

interface AreaChartProperties {
  data?: ChartRow[];
  title?: string;
  description?: string;
  xAxisKey?: string;
  yAxisKey?: string;
  areaKeys?: string[];
  showLegend?: boolean;
  showTooltip?: boolean;
  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  colors?: string[];
  responsive?: boolean;
  animation?: boolean;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  stacked?: boolean;
  fillOpacity?: number;
  minHeight?: string | number;
  height?: string | number;
  /** Phase 4.3: Stack offset for Recharts (none, expand, wiggle, silhouette) */
  stackOffset?: AreaStackOffset;
  /** Phase 4.3: Chart variant type (standard, percentage, stream) */
  chartVariant?: AreaChartVariant;
  lineKeys?: string[];
  /** Phase 2.6: Enhanced styling properties */
  strokeWidth?: number;
  strokeDasharray?: string;
  strokeOpacity?: number;
  lineType?: string;
  connectNulls?: boolean;
  showDots?: boolean;
  dotSize?: number;
  dotColor?: string;
  dotFill?: string;
  dotStrokeWidth?: number;
  dotShape?: string;
  showLine?: boolean;
  showTooltipContent?: boolean;
  timeZone?: string;
  timezone?: string;
}

type ChartMouseMoveState = {
  activePayload?: Array<{ payload?: ChartRow }>;
  activeTooltipIndex?: number | string | null;
};

type AreaClickPayload = {
  payload?: ChartRow;
  index?: number | string;
};

type LegendFormatterValue = {
  name?: unknown;
  key?: unknown;
};

type AreaCurveType =
  | "basis"
  | "basisClosed"
  | "basisOpen"
  | "bumpX"
  | "bumpY"
  | "bump"
  | "linear"
  | "linearClosed"
  | "natural"
  | "monotoneX"
  | "monotoneY"
  | "monotone"
  | "step"
  | "stepBefore"
  | "stepAfter";

const areaCurveTypes = new Set<AreaCurveType>([
  "basis",
  "basisClosed",
  "basisOpen",
  "bumpX",
  "bumpY",
  "bump",
  "linear",
  "linearClosed",
  "natural",
  "monotoneX",
  "monotoneY",
  "monotone",
  "step",
  "stepBefore",
  "stepAfter",
]);

const toOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const isAreaClickPayload = (value: unknown): value is AreaClickPayload => {
  return typeof value === "object" && value !== null;
};

const resolveAreaCurveType = (value: unknown): AreaCurveType =>
  typeof value === "string" && areaCurveTypes.has(value as AreaCurveType)
    ? value as AreaCurveType
    : "monotone";

const formatLegendValue = (value: unknown): React.ReactNode => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "object" && value !== null) {
    const legendValue = value as LegendFormatterValue;
    const displayValue = legendValue.name ?? legendValue.key;
    if (typeof displayValue === "string" || typeof displayValue === "number") {
      return displayValue;
    }
  }

  return String(value);
};

const defaultData = [
  { name: "Jan", value: 400, value2: 240 },
  { name: "Feb", value: 300, value2: 139 },
  { name: "Mar", value: 500, value2: 980 },
  { name: "Apr", value: 280, value2: 390 },
  { name: "May", value: 590, value2: 480 },
  { name: "Jun", value: 320, value2: 380 },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

export const AreaChart: React.FC<AreaChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const typedProperties = properties as AreaChartProperties;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Phase 2: Track container width for responsive breakpoint detection
  const [containerWidth, setContainerWidth] = useState(400);

  // Get dataSource configuration
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    // Don't seed with static template data — it's placeholder-scale and causes wrong Y-axis
    // until live data arrives. Charts show empty during loading instead.
  });

  // Component data store (populated by fetch-schema-data interactions)
  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);

  // Loading/error state: merge store + query template
  const loading = storeLoading || qtLoading;
  const error = storeError || qtError;

  // Data priority: Query template > Store data > Static data > Default data
  // When a live query is configured but still loading, use [] rather than stale static data —
  // static data in the template often has placeholder values at a completely different scale.
  const hasLiveQuery = hasQueryTemplate || !!templateId;
  const chartData = useMemo(() => {
    const hasLiveQueryTemplateData = hasLiveQuery && Array.isArray(queryTemplateData);

    if (hasLiveQueryTemplateData) {
      return queryTemplateData;
    }

    if (storeData.length > 0) {
      return storeData;
    }

    if (hasLiveQuery) {
      return queryTemplateData || [];
    }

    return typedProperties.data && Array.isArray(typedProperties.data) ? typedProperties.data : defaultData;
  }, [hasLiveQuery, queryTemplateData, storeData, typedProperties.data]);

  const normalizedChartData = useMemo(
    () => normalizeChartRows(chartData as ChartRow[]),
    [chartData]
  );

  // Enrich data with computed fields (derived columns like "profit = revenue - cost")
  const enrichedData = useMemo(
    () => enrichDataWithComputedFields(normalizedChartData, properties.computedFields as ComputedField[] | undefined),
    [normalizedChartData, properties.computedFields]
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [chartColors, setChartColors] = useState<string[]>(() => {
    const colors = (typedProperties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Phase 2: Track container width for responsive breakpoint detection
  // IMPORTANT: Use offsetWidth instead of getBoundingClientRect().width
  // because the editor canvas applies CSS transforms (scale) which affects
  // getBoundingClientRect but not offsetWidth. We want the actual layout
  // size, not the visually scaled size, for breakpoint detection.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      // Use offsetWidth for transform-independent measurement
      const width = container.offsetWidth;
      setContainerWidth(width);
    };

    // Initial measurement
    updateWidth();

    // Watch for size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Phase 2: Get breakpoint-specific configuration
  const responsiveChartConfig = properties.responsiveChartConfig as ResponsiveChartConfigInput | undefined;
  const breakpointConfig = getBreakpointChartConfig(containerWidth, responsiveChartConfig);

  // Phase 2: Apply breakpoint overrides to typedProperties
  const effectiveProperties = breakpointConfig ? {
    ...typedProperties,
    ...(breakpointConfig.showLegend !== undefined && { showLegend: breakpointConfig.showLegend }),
    ...(breakpointConfig.showGrid !== undefined && { showGrid: breakpointConfig.showGrid }),
    ...(breakpointConfig.showTooltip !== undefined && { showTooltip: breakpointConfig.showTooltip }),
    ...(breakpointConfig.showXAxis !== undefined && { showXAxis: breakpointConfig.showXAxis }),
    ...(breakpointConfig.showYAxis !== undefined && { showYAxis: breakpointConfig.showYAxis }),
    ...(breakpointConfig.margin !== undefined && { margin: breakpointConfig.margin }),
  } : typedProperties;

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (effectiveProperties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setChartColors(resolved);
  }, [effectiveProperties.colors, paletteChangeCounter]);

  // Get size configuration from actual rendered width (via ResizeObserver) for accurate responsive behavior
  const sizeConfig = getChartSizeConfig(containerWidth, toOptionalNumber(size?.height));
  const { isSmall } = sizeConfig;

  // Get responsive configuration
  const title = typedProperties.title === null || typedProperties.title === undefined || typedProperties.title === ""
    ? undefined
    : (typedProperties.title as string) || "Area Chart";
  const description = typedProperties.description as string;
  const userMargin = effectiveProperties.margin as ChartConfig['margin'];

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    userMargin,
    typedProperties.showLegend !== false,
    typedProperties.showGrid !== false
  );

  // Determine axis keys - use query definition if available, otherwise use properties or auto-detect
  const queryXKey = hasQueryTemplate && dataSource?.queryDefinition
    ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  // Validate xAxisKey exists in data — fall back to first non-numeric key
  const autoXKey = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(k => typeof normalizedChartData[0][k] === 'string' || k.includes('date') || k.includes('time'))
    : null;
  // Only use the explicit xAxisKey if it actually exists in the data — otherwise fall through to
  // queryXKey / autoXKey so stale template field names don't prevent auto-detection.
  const explicitXKey = typedProperties.xAxisKey as string | undefined;
  const explicitXKeyValid = explicitXKey && (normalizedChartData.length === 0 || explicitXKey in normalizedChartData[0]);
  const initialXAxisKey = (explicitXKeyValid ? explicitXKey : null)
    || (queryXKey && normalizedChartData.length > 0 && queryXKey in normalizedChartData[0] ? queryXKey : null)
    || autoXKey
    || queryXKey
    || "name";

  const xAxisKey = useMemo(() => {
    const firstRow = normalizedChartData[0] as Record<string, unknown> | undefined;
    const rawTemporalKey = isTemporalFieldName(initialXAxisKey)
      ? findRawTemporalField(firstRow, initialXAxisKey)
      : undefined;
    return rawTemporalKey || initialXAxisKey;
  }, [initialXAxisKey, normalizedChartData]);

  const sortedChartData = useMemo(
    () => sortChartRowsByXAxis(enrichedData as ChartRow[], xAxisKey),
    [enrichedData, xAxisKey]
  );

  // Write enriched data to chart hover store for linked DataText idle display.
  // Also publish under _templateNodeId for backwards-compat with old placements.
  const setChartData = useChartHoverStore((state) => state.setChartData);
  const templateNodeId = properties._templateNodeId as string | undefined;
  useEffect(() => {
    if (sortedChartData && sortedChartData.length > 0) {
      setChartData(component.id, sortedChartData);
      if (templateNodeId) setChartData(templateNodeId, sortedChartData);
    }
  }, [sortedChartData, component.id, templateNodeId, setChartData]);

  // Get area keys - support areaKeys, lineKeys (from backend), query definition, or auto-detect from data
  // Priority: explicit areaKeys > backend lineKeys > query definition (if keys exist in data) > auto-detect
  const queryDefKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : null;
  // Validate query definition keys actually exist in the data — template alias may differ from actual field names
  const validatedQueryDefKeys = queryDefKeys && normalizedChartData.length > 0
    ? queryDefKeys.filter(k => k in normalizedChartData[0])
    : queryDefKeys;
  // Validate explicit keys against actual data — fall through if they don't match
  const explicitAreaKeys = typedProperties.areaKeys || typedProperties.lineKeys || null;
  const validatedExplicitKeys = explicitAreaKeys && normalizedChartData.length > 0
    ? explicitAreaKeys.filter(k => k in normalizedChartData[0])
    : explicitAreaKeys;
  const detectedAreaKeys = useMemo(() => (
    (validatedExplicitKeys && validatedExplicitKeys.length > 0 ? validatedExplicitKeys : null)
    || (validatedQueryDefKeys && validatedQueryDefKeys.length > 0 ? validatedQueryDefKeys : null)
    || (normalizedChartData.length > 0
        ? Object.keys(normalizedChartData[0]).filter(key =>
            key !== xAxisKey &&
            typeof normalizedChartData[0][key] === 'number'
          )
        : ["value"])
  ), [normalizedChartData, validatedExplicitKeys, validatedQueryDefKeys, xAxisKey]);

  // Inject computed field names into keys (don't rely on auto-detection — null values fail typeof check)
  const computedFields = properties.computedFields as ComputedField[] | undefined;
  const computedFieldNames = useMemo(
    () => computedFields?.map(f => f.name) || [],
    [computedFields]
  );
  const areaKeys = useMemo(() => [
    ...detectedAreaKeys,
    ...computedFieldNames.filter(name => !detectedAreaKeys.includes(name)),
  ], [computedFieldNames, detectedAreaKeys]);

  // Dynamic margin adjustment: when Y-axis is hidden, set right margin to 0 for edge-to-edge
  // When Y-axis is showing, use default right margin for visual balance
  const baseMargin = responsiveConfig.margin || { top: 10, right: 20, bottom: 20, left: 20 };
  const adjustedMargin = {
    ...baseMargin,
    right: effectiveProperties.showYAxis === false ? 0 : (baseMargin.right ?? 20),
  };

  const chartConfig: ChartConfig = {
    title,
    data: sortedChartData,
    xAxisKey,
    yAxisKey: (typedProperties.yAxisKey as string) || "value",
    showLegend: responsiveConfig.showLegend,
    showTooltip: typedProperties.showTooltip !== false,
    showGrid: responsiveConfig.showGrid,
    colors: chartColors,
    responsive: typedProperties.responsive !== false,
    animation: typedProperties.animation !== false,
    margin: adjustedMargin,
  };

  // Phase 4.3: Extract stack offset and chart variant
  const stackOffset = typedProperties.stackOffset;
  // Phase 4.3: Apply stackId for all stacking modes (standard, percentage, stream)
  // stackId is needed when stacked=true OR when using stackOffset
  const stackId = (typedProperties.stacked || stackOffset) ? "stack" : undefined;

  // Phase 4.3: Y-axis configuration for percentage mode
  const isPercentageMode = stackOffset === 'expand';
  const explicitYAxisDomain = sanitizeNumericDomain(
    (typedProperties as Record<string, unknown>).yDomain
  );
  const tightYAxisDomain = useMemo(() => {
    if (explicitYAxisDomain || isPercentageMode || typedProperties.stacked || areaKeys.length === 0) {
      return undefined;
    }

    return computeTightNumericDomain(sortedChartData as ChartRow[], areaKeys);
  }, [areaKeys, explicitYAxisDomain, isPercentageMode, sortedChartData, typedProperties.stacked]);
  const yAxisDomain = explicitYAxisDomain || (isPercentageMode ? [0, 1] as [number, number] : tightYAxisDomain);

  const yAxisTickFormatter = isPercentageMode
    ? (value: number) => `${Math.round(value * 100)}%`
    : formatNumericAxisTick;

  // Phase 4.3: Tooltip formatter for percentage mode
  const tooltipFormatter = (value: unknown, name: unknown): [React.ReactNode, string] => {
    const numericValue = toOptionalNumber(value);
    const label = String(name);
    if (isPercentageMode) {
      return [`${((numericValue ?? 0) * 100).toFixed(1)}%`, label];
    }
    return [typeof value === 'number' ? value.toLocaleString() : String(value), label];
  };

  const displayTimeZone = useMemo(
    () => resolvePreferredTimeZone(
      effectiveProperties.timeZone as string | undefined,
      effectiveProperties.timezone as string | undefined,
      extractTimeZoneFromRecord(dataSource?.queryDefinition as ChartRow | undefined),
      extractTimeZoneFromRecord((sortedChartData[sortedChartData.length - 1] as ChartRow | undefined) ?? {}),
      getBrowserTimeZone()
    ),
    [dataSource?.queryDefinition, effectiveProperties.timeZone, effectiveProperties.timezone, sortedChartData]
  );

  // NOTE: Recharts' tickFormatter receives (value, tickIndex) where tickIndex is the
  // index within the *rendered* ticks array, NOT the data array index. When Recharts
  // auto-culls ticks to prevent overlap, tick index N may correspond to data point M
  // where M >> N. Using sortedChartData[tickIndex] would look up the wrong row.
  // Since effectiveXAxisKey already resolves to the raw temporal field, `value` is
  // already the correct timestamp — just format it directly.
  const xAxisTickFormatter = useCallback(
    (value: unknown) => {
      return formatAxisTick(value, { timeZone: displayTimeZone });
    },
    [displayTimeZone]
  );

  const tooltipLabelFormatter = useCallback(
    (label: unknown, payload: Array<{ payload?: ChartRow }> | undefined) => {
      const row = payload?.[0]?.payload ?? {};
      const rawTemporalKey = isTemporalFieldName(initialXAxisKey)
        ? findRawTemporalField(row, initialXAxisKey)
        : undefined;
      const labelToFormat = rawTemporalKey ? row?.[rawTemporalKey] : label;
      const rowTimeZone = extractTimeZoneFromRecord(row);
      return formatAxisTick(labelToFormat, { timeZone: rowTimeZone || displayTimeZone });
    },
    [displayTimeZone, initialXAxisKey]
  );

  const chartDomainKey = yAxisDomain
    ? `${yAxisDomain.map((value) => value.toString()).join('-')}-${areaKeys.join(',')}`
    : `${component.id}-auto-${areaKeys.join(',')}`;

  // Chart hover store handlers for compound chart integration
  const setHoverData = useChartHoverStore((state) => state.setHoverData);
  const clearHoverData = useChartHoverStore((state) => state.clearHoverData);

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  const handleChartMouseMove = useCallback(
    (e: ChartMouseMoveState | undefined) => {
      if (e?.activePayload?.[0]?.payload) {
        const payload = e.activePayload[0].payload;
        const index = toOptionalNumber(e.activeTooltipIndex) ?? null;
        setHoverData(component.id, payload, index);
        if (templateNodeId) setHoverData(templateNodeId, payload, index);
      }
    },
    [component.id, templateNodeId, setHoverData]
  );

  const handleChartMouseLeave = useCallback(() => {
    clearHoverData(component.id);
    if (templateNodeId) clearHoverData(templateNodeId);
  }, [component.id, templateNodeId, clearHoverData]);

  const handleAreaClick = useCallback(
    (key: string) => (data: unknown) => {
      const payload = isAreaClickPayload(data) ? data.payload ?? {} : {};
      const index = isAreaClickPayload(data) ? toOptionalNumber(data.index) : undefined;
      void handleChartClick(payload, key, index);
    },
    [handleChartClick]
  );

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 40 : sizeConfig.isSmall ? 70 : 90;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: typedProperties.minHeight || `${dynamicMinimum + (responsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {responsiveConfig.showTitle && properties.showInternalTitle !== false && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="area" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : normalizedChartData && normalizedChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart
              key={chartDomainKey}
              data={chartConfig.data}
              margin={chartConfig.margin}
              style={{ userSelect: isPreview ? "auto" : "none" }}
              stackOffset={stackOffset}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
            {/* Gradient definitions for fading effect */}
            <defs>
              {areaKeys.map((key: string, index: number) => {
                const color = chartColors?.[index % chartColors.length] || defaultColors[index % defaultColors.length];
                return (
                  <linearGradient key={`gradient-${key}`} id={`colorGradient-${key}-${component.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            {chartConfig.showGrid && effectiveProperties.showXAxis !== false && effectiveProperties.showYAxis !== false && (
              <CartesianGrid strokeDasharray="3 3" />
            )}
            <XAxis
              dataKey={chartConfig.xAxisKey}
              tick={effectiveProperties.showXAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              tickFormatter={xAxisTickFormatter}
              axisLine={effectiveProperties.showXAxis !== false}
              tickLine={effectiveProperties.showXAxis !== false}
              padding={{ left: 10, right: 10 }}
              label={effectiveProperties.xAxisLabel && effectiveProperties.showXAxis !== false && !isSmall ? { value: String(effectiveProperties.xAxisLabel), position: 'insideBottom', offset: -5, fontSize: responsiveConfig.axisFontSize } : undefined}
            />
            <YAxis
              hide={effectiveProperties.showYAxis === false}
              width={responsiveConfig.yAxisWidth}
              tick={effectiveProperties.showYAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              axisLine={effectiveProperties.showYAxis !== false}
              tickLine={effectiveProperties.showYAxis !== false}
              label={effectiveProperties.yAxisLabel && effectiveProperties.showYAxis !== false && !isSmall ? { value: String(effectiveProperties.yAxisLabel), angle: -90, position: 'insideLeft', fontSize: responsiveConfig.axisFontSize } : undefined}
              domain={yAxisDomain}
              tickFormatter={yAxisTickFormatter}
            />
            {chartConfig.showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
                content={effectiveProperties.showTooltipContent === false ? () => null : undefined}
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
                cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }}
              />
            )}
            {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{
                  fontSize: `${responsiveConfig.legendFontSize}px`,
                  paddingTop: '35px'
                }}
                formatter={formatLegendValue}
              />
            )}
            {areaKeys.map((key: string, index: number) => {
              const areaColor = chartColors?.[index % chartColors.length] || defaultColors[index % defaultColors.length];

              // Phase 2.6: Configure dot/marker display
              const dotConfig = typedProperties.showDots ? {
                r: (typedProperties.dotSize as number) || 4,
                fill: (typedProperties.dotFill as string) || areaColor,
                stroke: (typedProperties.dotColor as string) || areaColor,
                strokeWidth: (typedProperties.dotStrokeWidth as number) || 1,
              } : false;

              // Phase 2.6: Determine stroke visibility
              const showStroke = typedProperties.showLine !== false;

              // Determine if this series has navigation configured
              const seriesNavigation = getNavigationForElement(key);
              const hasSeriesNavigation = isNavigable && seriesNavigation;

              return (
                <Area
                  key={key}
                  type={resolveAreaCurveType(typedProperties.lineType)}
                  dataKey={key}
                  name={humanizeFieldName(key)}
                  stackId={stackId}
                  stroke={showStroke ? areaColor : 'transparent'}
                  strokeWidth={(typedProperties.strokeWidth as number) || 2}
                  strokeDasharray={(typedProperties.strokeDasharray as string) || undefined}
                  strokeOpacity={(typedProperties.strokeOpacity as number) ?? 1}
                  fill={`url(#colorGradient-${key}-${component.id})`}
                  animationDuration={chartConfig.animation ? 1500 : 0}
                  dot={dotConfig}
                  connectNulls={typedProperties.connectNulls ?? false}
                  onClick={handleAreaClick(key)}
                  cursor={hasSeriesNavigation ? 'pointer' : 'default'}
                />
              );
            })}
          </RechartsAreaChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
